import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  resetMemberPassword,
  sendInvitation,
  setMemberStatus,
  updateMemberEmail,
  validateApiResponse,
} from '../../../services';
import { MEMBER_ACTION, MEMBER_BULK_ACTION, MEMBER_STATUS_REQUEST } from '../constants/memberActions';
import { MEMBER_STATUS } from '../constants/copy';
import { INVITATION_VALID_DAYS } from '../constants/memberDetail';

const invite = { call: (m) => sendInvitation(m.id), copy: 'invite' };
const suspend = { call: (m) => setMemberStatus(m.id, MEMBER_STATUS_REQUEST.SUSPEND), copy: 'suspend' };

/** What each row action calls on the (mock) service, and the toast copy keys of its outcome. */
const ROW_ACTIONS = {
  [MEMBER_ACTION.RESET_PASSWORD]: { call: (m) => resetMemberPassword(m.id), copy: 'resetPassword' },
  [MEMBER_ACTION.SUSPEND]: suspend,
  [MEMBER_ACTION.REACTIVATE]: {
    call: (m) => setMemberStatus(m.id, MEMBER_STATUS_REQUEST.REACTIVATE),
    copy: 'reactivate',
  },
  [MEMBER_ACTION.SEND_INVITATION]: invite,
  // Resending to someone already Invited has its own toast (they are not "moved" anywhere).
  [MEMBER_ACTION.RESEND_INVITATION]: { call: invite.call, copy: 'resend' },
  [MEMBER_ACTION.REVOKE_INVITATION]: {
    call: (m) => setMemberStatus(m.id, MEMBER_STATUS_REQUEST.REVOKE),
    copy: 'revoke',
  },
};

const BULK_ACTIONS = {
  [MEMBER_BULK_ACTION.SEND_INVITATIONS]: invite,
  [MEMBER_BULK_ACTION.SUSPEND]: suspend,
  [MEMBER_BULK_ACTION.REMOVE]: {
    call: (m) => setMemberStatus(m.id, MEMBER_STATUS_REQUEST.REMOVE),
    copy: 'remove',
  },
};

/**
 * Runs the Members list actions against the service and reports the outcome with toasts (a title
 * on every toast; the name or the count in the message so identical toasts are not dropped).
 * Row and bulk lifecycle changes only. Suspend (row and bulk), Remove and Send invitations ask
 * first: the row Suspend through `pendingSuspendRow`, the bulk ones through `pendingSuspend`,
 * `pendingRemove` and `pendingInvite`; Edit email opens `pendingEmail`. The invitation and remove
 * FLOWS belong to CT-5575 / CT-5574. `runRowAction` resolves `true` when the action worked, and
 * `null` when it only opened a question (nothing happened yet). A bulk run is exclusive: while
 * one is in flight `isBusy` is true and a second one is refused.
 *
 * @param {object} options
 * @param {() => void} options.clearSelection Called after a bulk action completes with no failure.
 * @param {(ids: string[]) => void} [options.deselect] Called with the members a bulk action did
 *   change when some failed, so the failed ones stay selected for another try.
 */
export const useMemberRowActions = ({ clearSelection, deselect } = {}) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [pendingSuspendRow, setPendingSuspendRow] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [pendingSuspend, setPendingSuspend] = useState([]);
  const [pendingRemove, setPendingRemove] = useState([]);
  const [pendingInvite, setPendingInvite] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  // The ref refuses a second run before React re-renders with `isBusy`.
  const busyRef = useRef(false);
  // Row actions in flight (`action:member`): the same one twice at once runs once.
  const inFlightRef = useRef(new Set());

  const fail = useCallback(
    (copy, message) => {
      toast.error({ title: t(`members.feedback.${copy}.errorTitle`), message, canClose: true });
    },
    [t, toast],
  );

  const attempt = useCallback(async (call, member) => {
    try {
      validateApiResponse(await call(member), '');
      return null;
    } catch (err) {
      return err?.message || '';
    }
  }, []);

  const toastSuccess = useCallback(
    (copy, member, extra) => {
      toast.success({
        title: t(`members.feedback.${copy}.title`, { name: member.displayName }),
        message: t(`members.feedback.${copy}.message`, {
          name: member.displayName,
          email: member.email,
          days: INVITATION_VALID_DAYS,
        }),
        canClose: true,
        ...extra,
      });
    },
    [t, toast],
  );

  // Revoking asks nothing: "Send again" in the toast undoes it by inviting the person once more.
  const sendAgain = useCallback(
    async (member) => {
      const key = `invite:${member.id}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      let error;
      try {
        error = await attempt(invite.call, member);
      } finally {
        inFlightRef.current.delete(key);
      }
      if (error !== null) {
        fail('invite', error || t('members.feedback.invite.errorMessage', { name: member.displayName }));
        return;
      }
      toastSuccess('invite', member);
    },
    [attempt, fail, t, toastSuccess],
  );

  const execute = useCallback(
    async (actionId, member) => {
      const action = ROW_ACTIONS[actionId];
      if (!action) return false;
      // Every way to send the invitation shares one key: they must not run together for one member.
      const key = `${action.copy === 'invite' || action.copy === 'resend' ? 'invite' : actionId}:${member.id}`;
      if (inFlightRef.current.has(key)) return false;
      inFlightRef.current.add(key);
      let error;
      try {
        error = await attempt(action.call, member);
      } finally {
        inFlightRef.current.delete(key);
      }
      if (error !== null) {
        fail(action.copy, error || t(`members.feedback.${action.copy}.errorMessage`, { name: member.displayName }));
        return false;
      }
      const isRevoke = actionId === MEMBER_ACTION.REVOKE_INVITATION;
      toastSuccess(
        action.copy,
        member,
        isRevoke ? { action: t('members.feedback.revoke.action'), onAction: () => sendAgain(member) } : undefined,
      );
      return true;
    },
    [attempt, fail, sendAgain, t, toastSuccess],
  );

  const runRowAction = useCallback(
    async (actionId, member) => {
      // Suspending signs the person out everywhere: it asks first. Edit email is a form.
      if (actionId === MEMBER_ACTION.SUSPEND) {
        setPendingSuspendRow(member);
        return null;
      }
      if (actionId === MEMBER_ACTION.EDIT_EMAIL) {
        setPendingEmail(member);
        return null;
      }
      return execute(actionId, member);
    },
    [execute],
  );

  const confirmSuspendRow = useCallback(async () => {
    const ok = await execute(MEMBER_ACTION.SUSPEND, pendingSuspendRow);
    // A failed suspension keeps the row selected, like a failed bulk one.
    if (ok) deselect?.([pendingSuspendRow.id]);
    return ok;
  }, [execute, pendingSuspendRow, deselect]);

  const cancelSuspendRow = useCallback(() => setPendingSuspendRow(null), []);

  const saveEmail = useCallback(
    async (email) => {
      const member = pendingEmail;
      const error = await attempt((m) => updateMemberEmail(m.id, email), member);
      if (error !== null) {
        fail('editEmail', error || t('members.feedback.editEmail.errorMessage', { name: member.displayName }));
        return false;
      }
      // Never invited: nothing went out, so only the address changed.
      const copy = member.status === MEMBER_STATUS.NOT_INVITED ? 'editEmailNotSent' : 'editEmail';
      toast.success({
        title: t(`members.feedback.${copy}.title`),
        message: t(`members.feedback.${copy}.message`, { email, oldEmail: member.email }),
        canClose: true,
      });
      deselect?.([member.id]);
      return true;
    },
    [attempt, deselect, fail, pendingEmail, t, toast],
  );

  const cancelEmail = useCallback(() => setPendingEmail(null), []);

  const runBulk = useCallback(
    async (actionId, members) => {
      const action = BULK_ACTIONS[actionId];
      if (busyRef.current || !action) return;
      busyRef.current = true;
      setIsBusy(true);
      const errors = [];
      const done = [];
      try {
        for (const member of members) {
          const error = await attempt(action.call, member);
          if (error === null) done.push(member.id);
          else errors.push(error);
        }
      } finally {
        busyRef.current = false;
        setIsBusy(false);
      }

      if (done.length > 0) {
        toast.success({
          title: t(`members.feedback.${action.copy}.bulkTitle`, { count: done.length }),
          message: t(`members.feedback.${action.copy}.bulkMessage`, { count: done.length }),
          canClose: true,
        });
      }
      if (errors.length) {
        fail(action.copy, errors[0] || t(`members.feedback.${action.copy}.bulkErrorMessage`, { count: errors.length }));
        // The failed ones stay selected so the action can be tried again.
        deselect?.(done);
      } else {
        clearSelection?.();
      }
    },
    [attempt, clearSelection, deselect, fail, t, toast],
  );

  const runBulkAction = useCallback(
    async (actionId, members) => {
      // Bulk suspend is two steps like Remove: check who, then type the word.
      if (actionId === MEMBER_BULK_ACTION.SUSPEND) {
        setPendingSuspend(members);
        return;
      }
      if (actionId === MEMBER_BULK_ACTION.REMOVE) {
        setPendingRemove(members);
        return;
      }
      // Emails go out at once and cannot be recalled: the invitations are checked first.
      if (actionId === MEMBER_BULK_ACTION.SEND_INVITATIONS) {
        setPendingInvite(members);
        return;
      }
      await runBulk(actionId, members);
    },
    [runBulk],
  );

  const confirmSuspend = useCallback(async () => {
    if (busyRef.current) return;
    await runBulk(MEMBER_BULK_ACTION.SUSPEND, pendingSuspend);
    setPendingSuspend([]);
  }, [pendingSuspend, runBulk]);

  const cancelSuspend = useCallback(() => setPendingSuspend([]), []);

  const confirmRemove = useCallback(async () => {
    // A second click while the removal runs must not close the dialog under it.
    if (busyRef.current) return;
    await runBulk(MEMBER_BULK_ACTION.REMOVE, pendingRemove);
    setPendingRemove([]);
  }, [pendingRemove, runBulk]);

  const cancelRemove = useCallback(() => setPendingRemove([]), []);

  const confirmInvite = useCallback(async () => {
    if (busyRef.current) return;
    await runBulk(MEMBER_BULK_ACTION.SEND_INVITATIONS, pendingInvite);
    setPendingInvite([]);
  }, [pendingInvite, runBulk]);

  const cancelInvite = useCallback(() => setPendingInvite([]), []);

  return useMemo(
    () => ({
      runRowAction,
      runBulkAction,
      pendingSuspendRow,
      confirmSuspendRow,
      cancelSuspendRow,
      pendingEmail,
      saveEmail,
      cancelEmail,
      pendingSuspend,
      confirmSuspend,
      cancelSuspend,
      pendingRemove,
      confirmRemove,
      cancelRemove,
      pendingInvite,
      confirmInvite,
      cancelInvite,
      isBusy,
    }),
    [
      runRowAction,
      runBulkAction,
      pendingSuspendRow,
      confirmSuspendRow,
      cancelSuspendRow,
      pendingEmail,
      saveEmail,
      cancelEmail,
      pendingSuspend,
      confirmSuspend,
      cancelSuspend,
      pendingRemove,
      confirmRemove,
      cancelRemove,
      pendingInvite,
      confirmInvite,
      cancelInvite,
      isBusy,
    ],
  );
};
